import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { getArticleById } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  const article = getArticleById(id || '');

  useEffect(() => {
    if (article) {
      document.title = `${article.title} - 黄油游戏库`;
    }
  }, [article]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
            </div>
            <p className="text-foreground-600 mb-4">文章未找到</p>
            <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm font-medium cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <div className="px-4 md:px-6 bg-background-50 border-b border-background-200/70">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '文章', href: '/article/art-1' },
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <article className="py-10 md:py-14 px-4 md:px-6 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <header className="mb-8">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                {article.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full">
                    {tag}
                  </span>
                ))}
              </div>
              <h1 className="text-2xl md:text-4xl font-heading text-foreground-900 leading-tight mb-4">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <div className="flex items-center gap-1.5">
                  <div className="w-5 h-5 rounded-full bg-secondary-200 flex items-center justify-center">
                    <i className="ri-user-line text-secondary-600 text-xs"></i>
                  </div>
                  <span>{article.author}</span>
                </div>
                <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                <div className="flex items-center gap-1.5">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-calendar-line text-foreground-400 text-xs"></i>
                  </div>
                  <span>{article.date}</span>
                </div>
                <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                <div className="flex items-center gap-1.5">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-time-line text-foreground-400 text-xs"></i>
                  </div>
                  <span>{article.readTime}</span>
                </div>
              </div>
            </header>

            <div className="mb-10 rounded-lg overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                title={article.title}
                className="w-full aspect-[2/1] object-cover object-top rounded-lg"
              />
            </div>

            <div className="prose prose-neutral max-w-none">
              {article.content.map((block, index) => {
                switch (block.type) {
                  case 'heading':
                    return (
                      <h2 key={index} className="text-xl md:text-2xl font-heading text-foreground-900 mt-8 mb-4">
                        {block.value}
                      </h2>
                    );
                  case 'paragraph':
                    return (
                      <p key={index} className="text-base text-foreground-700 leading-relaxed mb-4">
                        {block.value}
                      </p>
                    );
                  case 'quote':
                    return (
                      <blockquote key={index} className="border-l-4 border-primary-400 bg-primary-50/50 pl-5 py-3 my-6 rounded-r-lg">
                        <p className="text-base text-foreground-700 leading-relaxed italic">
                          {block.value}
                        </p>
                      </blockquote>
                    );
                  case 'list':
                    return (
                      <ul key={index} className="list-disc pl-6 mb-4 space-y-1">
                        {block.items?.map((item, i) => (
                          <li key={i} className="text-base text-foreground-700 leading-relaxed">
                            {item}
                          </li>
                        ))}
                      </ul>
                    );
                  default:
                    return null;
                }
              })}
            </div>

            <div className="mt-12 pt-8 border-t border-background-200/70">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <p className="text-sm text-foreground-500">觉得这篇文章有用？</p>
                  <p className="text-sm text-foreground-500">分享给更多游戏爱好者吧！</p>
                </div>
                <div className="flex items-center gap-3">
                  {[
                    { icon: 'ri-wechat-line', label: '微信' },
                    { icon: 'ri-weibo-line', label: '微博' },
                    { icon: 'ri-qq-line', label: 'QQ' },
                    { icon: 'ri-link', label: '复制链接' },
                  ].map((platform) => (
                    <button
                      key={platform.label}
                      className="w-9 h-9 rounded-full bg-background-100 flex items-center justify-center hover:bg-primary-100 transition-colors duration-200 cursor-pointer"
                      title={platform.label}
                    >
                      <i className={`${platform.icon} text-foreground-600 text-sm`}></i>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />

      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: article.title,
          description: article.excerpt,
          author: { '@type': 'Person', name: article.author },
          datePublished: article.date,
          image: article.image,
          publisher: {
            '@type': 'Organization',
            name: '黄油游戏库',
          },
        })}
      </script>
    </>
  );
}